import { useEffect, useRef } from "react";
import type { CommutePlan } from "../shared/types";
import { useAlertDiagnostics } from "./alertDiagnostics";
import { report } from "./report";

const ACTIVE_HEARTBEAT_INTERVAL_MS = 60_000;
const INACTIVE_HEARTBEAT_INTERVAL_MS = 5 * 60_000;

type DiagnosticPayload = Record<string, unknown>;
type JourneyState = Record<string, string | number | boolean | null>;

/** The train facts needed to reconstruct what the rider saw and chose. */
export function planDiagnostic(plan: CommutePlan | null): DiagnosticPayload | null {
	if (!plan) {
		return null;
	}
	return {
		id: plan.id,
		tripId: plan.tripId,
		route: plan.routeName,
		headsign: plan.headsign,
		boardingMs: plan.boarding.predictedMs,
		boardingSource: plan.boarding.source,
		...(plan.boarding.retainedAtMs != null ? { boardingRetainedAtMs: plan.boarding.retainedAtMs } : {}),
		delaySeconds: plan.boarding.delaySeconds,
		track: [...plan.upstream, plan.boarding, ...plan.downstream].map((stop) => ({
			stopId: stop.stopId,
			stopName: stop.stopName,
			predictedMs: stop.predictedMs,
		})),
		// Where it gets him and when, so a reader who is not holding the phone has the whole leg.
		destinations: plan.destinations.map((stop) => ({ stopId: stop.stopId, stopName: stop.stopName, predictedMs: stop.predictedMs })),
		leaveByMs: plan.leaveByMs,
		startByMs: plan.startByMs,
		stationArrivalMs: plan.stationArrivalMs,
		platformArrivalMs: plan.platformArrivalMs,
		finalArrivalMs: plan.finalArrivalMs,
		status: plan.status,
		urgency: plan.urgency,
		feasible: plan.feasible,
	};
}

export function changedJourneyKeys(previous: JourneyState, next: JourneyState): string[] {
	return Object.keys(next).filter((key) => previous[key] !== next[key]);
}

/** Inactive screens need liveness, not a minute-by-minute journey trace. */
export function heartbeatIntervalFor(phase: unknown): number {
	return phase === "planning" || phase === "arrived" ? INACTIVE_HEARTBEAT_INTERVAL_MS : ACTIVE_HEARTBEAT_INTERVAL_MS;
}

/**
 * Keep a short-lived server timeline while the installed app is in active use.
 *
 * The latest payload lives in a ref so location and train predictions can change freely without
 * resetting the minute timer. State transitions are separate rows because those are the moments
 * a heartbeat cadence is most likely to miss.
 */
export function useJourneyDiagnostics(enabled: boolean, payload: DiagnosticPayload, journeyState: JourneyState): void {
	const alertDiagnostics = useAlertDiagnostics();
	const latestRef = useRef<DiagnosticPayload>({});
	const previousStateRef = useRef<JourneyState | null>(null);
	const sentInitialRef = useRef(false);
	// On every row, not only on transitions: a heartbeat that cannot say where he is leaves anyone
	// reading the record afterwards to guess, and guessing is what put the wrong train in a digest.
	latestRef.current = { ...payload, state: journeyState, alertDiagnostics };
	const heartbeatIntervalMs = heartbeatIntervalFor(journeyState.phase);

	useEffect(() => {
		if (!enabled) {
			sentInitialRef.current = false;
			previousStateRef.current = null;
			return;
		}

		const heartbeat = (reason: "opened" | "minute" | "visible") => {
			if (document.visibilityState === "visible") {
				report("heartbeat", { reason, ...latestRef.current });
			}
		};
		if (!sentInitialRef.current) {
			sentInitialRef.current = true;
			heartbeat("opened");
		}
		// A hidden iOS web view may be suspended entirely. This is a foreground cadence, not a
		// promise that iOS will wake an inactive app every five minutes.
		const timer = window.setInterval(() => heartbeat("minute"), heartbeatIntervalMs);
		const onVisibility = () => {
			report("state-change", { changed: ["visibility"], visibility: document.visibilityState, ...latestRef.current });			if (document.visibilityState === "visible") {
				heartbeat("visible");
			}
		};
		const onConnectivity = () => {
			report("state-change", { changed: ["online"], online: navigator.onLine, ...latestRef.current });
		};
		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("online", onConnectivity);
		window.addEventListener("offline", onConnectivity);
		return () => {
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("online", onConnectivity);
			window.removeEventListener("offline", onConnectivity);
		};
	}, [enabled, heartbeatIntervalMs]);

	const stateKey = JSON.stringify(journeyState);
	useEffect(() => {
		if (!enabled) {
			return;
		}
		const previous = previousStateRef.current;
		previousStateRef.current = journeyState;
		if (!previous) {
			return;
		}
		const changed = changedJourneyKeys(previous, journeyState);
		if (changed.length > 0) {
			report("state-change", { changed, ...latestRef.current });
		}
	}, [enabled, stateKey]);
}